// Copyright 2025-2026 NXGN Management, LLC. All Rights Reserved.
import { useState } from 'react'
import { useQuery } from 'react-query'
import { Button, Flex, useShowHide } from '@teamnine/pcj-ui-v2'
import { Add } from '@/utils/material-icons'
import { useNavigate, useParams, Outlet } from 'react-router-dom'

import { Customer as ICustomer, getCustomer } from '@/services/customers'
import {
  Tenant as ITenant,
  sortActiveAndArchivedTenants,
} from '@/services/practices'
import { getTagColor, getTagLabel } from '@/utils/helpers'
import { useRouteTabs } from '@/app/shared/hooks/useRouteTabs'
import Page from '@/app/shared/components/Layout/Page'
import PageHeader from '@/app/shared/components/PageHeader'
import Panel from '@/app/shared/components/Panel'
import CreatePracticeFromTemplateModal from '@/app/CustomerManagement/components/CreatePracticeFromTemplateModal'

const PRACTICE_PANES = [
  { key: 'active', label: 'Active' },
  { key: 'archived', label: 'Archived' },
]

export default function Customer() {
  const { customerId } = useParams()
  const navigate = useNavigate()
  const createPracticeModal = useShowHide()
  const pageTabs = useRouteTabs({ panes: PRACTICE_PANES })

  const [practices, setPractices] = useState<ITenant[]>([])
  const [archivedPractices, setArchivedPractices] = useState<ITenant[]>([])
  const [currentCustomer, setCurrentCustomer] = useState<
    ICustomer | undefined
  >()

  const { isLoading } = useQuery(['getCustomer', customerId], async () => {
    if (!customerId) return null
    const data = await getCustomer(customerId)
    setCurrentCustomer(data.customer)
    const { activeTenants, archivedTenants } = sortActiveAndArchivedTenants(
      data.tenants
    )
    setPractices(activeTenants)
    setArchivedPractices(archivedTenants)
    return data
  })

  return (
    <Page>
      <Page.Content>
        <Panel useContent={false} p={16}>
          <Flex direction="column" gap={16}>
            <PageHeader
              title={currentCustomer?.name ?? ''}
              tag={{
                label: getTagLabel(currentCustomer),
                color: getTagColor(currentCustomer),
              }}
              action={
                pageTabs.activeKey === 'active' && (
                  <Button
                    aria-label="Add new practice"
                    data-testid="customer-management-add-practice-btn"
                    icon={<Add sx={{ fontSize: 12 }} />}
                    onClick={createPracticeModal.show}
                  >
                    Add New Practice
                  </Button>
                )
              }
              breadcrumbItems={[
                {
                  label: 'Customer Management',
                  onClick: () => navigate('/customer-management'),
                },
                {
                  label: currentCustomer?.name ?? '',
                },
              ]}
              tabs={{
                key: 'customer-practices',
                items: pageTabs.panes.map((pane) => {
                  const count =
                    pane.key === 'active'
                      ? practices.length
                      : archivedPractices.length
                  return {
                    key: pane.key,
                    label: `${pane.label} (${count})`,
                  }
                }),
                activeKey: pageTabs.activeKey,
                onChange: pageTabs.onChange,
              }}
              isLoading={isLoading}
            />
            <Outlet
              context={{
                practices,
                archivedPractices,
                currentCustomer,
                isLoading,
              }}
            />
          </Flex>
        </Panel>
      </Page.Content>
      {createPracticeModal.visible && (
        <CreatePracticeFromTemplateModal
          modal={createPracticeModal}
          customer={currentCustomer}
          practices={practices}
        />
      )}
    </Page>
  )
}
